import type {
  DataVizDemographicCountDTO,
  DataVizDemographicPercentDTO,
  EnumerationsDataVisualizationResultsBy,
} from 'client'
import type { ILabelValue } from '~/constants/types'
import type { Chart } from 'chart.js'
import { getComputedColor } from '~/helpers/theme'

export type TCode = 'eeoCode' | 'jobGroup' | 'description' | 'demographic'

export type BerkshireBarChart = Chart<
  'bar',
  number[] | number[][],
  string | string[]
>
export interface IDataSet {
  label?: string
  backgroundColor?: string[] | string
  data: number[]
  maxBarThickness?: number
  barThickness?: number
  borderColor?: string
  borderWidth?: string | number
  codes?: string[]
  stack?: string
}

export interface ILegendLabelData {
  label?: IDataSet['label']
  color?: IDataSet['backgroundColor']
}

export interface ISimpleLegendItem {
  label: string
  color: string
}

// Doughnut Chart
export interface IDoughnutHole {
  color: string
  percentage?: number | null
  number?: number
  title?: string
  label?: string
}

export interface IAppDoughnutChartData {
  title: string
  subtitle: string
  dataSets: IDataSet[]
  labels: string[] | { label: string; color: string | undefined }[]
  doughnutHole?: IDoughnutHole
  valueLabel?: string
  valueType?: ValueType
}

export interface IAppMiniDoughnutChartDataSet {
  label: string
  subLabel: string
  planID: string
  planIdentifier: string
  sort1: number | string
  sort2: number | string
  sortName: string
  data: [IAppDoughnutChartData, IAppDoughnutChartData]
}

export interface IAppMiniDoughnutChartGroupData {
  // the two doughnuts
  dataSets: IAppMiniDoughnutChartDataSet[]
  // title/sub for mini donut chart list
  title: string
  subtitle: string
}

export interface IDrillDownData {
  labels: string[]
  dataSets: IDataSet[]
}

export interface IChunks {
  labels: (string | string[])[]
  dataSets: IDataSet[]
  codes: string[] | undefined
}

// Group Bar Chart
export interface IGroupBarChartData {
  title: string
  subtitle: string
  dataSets: IDataSet[]
  labels: (string | string[])[]
  sortOptions?: ILabelValue[]
  valueType: ValueType
}

export interface IStackedTooltipLabel {
  positive: string
  negative: string
}
export interface IStackedBarChartData extends IGroupBarChartData {
  chunks?: IChunks
  isStacked: boolean
  tooltipLabel?: string
  stackedToolTipLabel?: IStackedTooltipLabel
  stack?: string
  dataId?: string
  labelsSeparated: string[]
  dataSetsSeparated: IGroupBarChartData['dataSets']
}

export interface ILocation {
  city: string
  state: string
  percentage: number
}
export interface IGoal {
  demographic: string
  percentage?: number
  quantity?: number
  location?: ILocation[]
}

export interface IWordCloudData {
  goals: IGoal[]
  labels: string[]
  data: number[]
  goalTitle: 'string'
}

export interface IDrillDownTile {
  background: string
  title: string
  display: string
  subDisplay?: string
}
export interface IDrillDownBarChartData extends IGroupBarChartData {
  tiles: IDrillDownTile[]
  data?: IDrillDownData
  chunks?: IChunks
  breadcrumbs?: string[]
  dataId?: string
  wordCloudData?: IWordCloudData
  totalPlans?: number
}

export interface IDrillDownState extends IDrillDownBarChartData {
  isOpen: boolean
  level: number
  maxLevel: number
  totalPlans: number
}

// Dashboard Tiles
export type TBackground =
  | 'blue'
  | 'orange'
  | 'purple'
  | 'blue-green'
  | 'dark-gray'
  | 'red'
export type TDirection = 'up' | 'down'

export interface ITilesData {
  title: string
  background: TBackground
  numberDisplay?: number | string | null
  direction?: TDirection | null
  subText?: string
  icon?: string
  strataNumber?: number
}

interface IReportTilesData extends ITilesData {
  type: 'report'
  reportName?: string
  reportID?: string
  planIDs: string[]
}

interface ISummaryTilesData extends ITilesData {
  type: 'summary'
  openSettingsModal: () => void
  loading: boolean
  download: () => Promise<void>
}

export type ReportTilesData = IReportTilesData | ISummaryTilesData

// Drill Down
export interface IBreadcrumbNames {
  default: string
  level1: string
  level2: string
}

export type TDrillDownChart =
  | 'total-minority-race'
  | 'individual-race'
  | 'gender'

export type TMinority = 'Min' | 'Non-Min' | 'Unk'
export type TRace = 'Wht' | 'Blk' | 'Asi' | 'Two' | 'Pac' | 'Ind' | 'His'
export type TGender = 'Fem' | 'Mal'
export type TDemographic = TMinority | TRace | TGender
export type TJobGroupCategory = 'withGoals' | 'withoutGoals' | 'AI' | 'No AI'

// Brady Bunch Tile
export interface IBradyBunchTile {
  eeoCode: string
  eeoCodeName: string
  eeoCodeGoals: string | undefined // goals for this code
  eeoCodeJobGroups: string // number of jgs in this eeocode
}

interface IJobTitle {
  jobTitle: string
  count: number
}
export interface IGoalsByJobTitleForJobGroup {
  jobTitles: IJobTitle[]
  goals: IGoal[]
  jobTitleCount: number
  lowestMinorityJobTitle: string
  lowestFemaleJobTitle: string
}

export interface IDoughnutTile {
  title: string
  number: number
}

// at least one tile needs to be defined
export interface IDoughnutTiles {
  tile1: IDoughnutTile
  tile2?: IDoughnutTile
  tile3?: IDoughnutTile
  tile4?: IDoughnutTile
  tile5?: IDoughnutTile
}

export const OCountLocation = {
  POSITIVE_GENDER_COUNTS: 'positiveGenderCounts',
  NEGATIVE_GENDER_COUNTS: 'negativeGenderCounts',
  POSITIVE_RACE_COUNTS: 'positiveRaceCounts',
  NEGATIVE_RACE_COUNTS: 'negativeRaceCounts',
} as const

export type TCountLocation =
  (typeof OCountLocation)[keyof typeof OCountLocation]
export interface IGhostDonut {
  holeTitle?: string
  holeSubtitle: string
}

export type ValueType = 'count' | 'percent'

export interface ICreateDoughnutChartOptions {
  colors: IDemographicColor[]
  title: string
  subtitle: string
  checkResultsToDisplayFor?: boolean
  doughnutHole:
    | IDoughnutHole
    | ((
        value:
          | DataVizDemographicCountDTO[]
          | DataVizDemographicPercentDTO[]
          | null
          | undefined
      ) => IDoughnutHole)
  ghostDonut: IGhostDonut
  valueLabel?: string
  valueType: ValueType
}

export interface ICreateDoughnutChartData {
  demographicCounts?: DataVizDemographicCountDTO[]
  demographicPercents?: DataVizDemographicPercentDTO[]
}

export const ODemographicChartLabels = {
  MIN: 'Min',
  NON_MIN: 'Non-Min',
  ASI: 'Asi',
  BLK: 'Blk',
  HIS: 'His',
  IND: 'Ind',
  PAC: 'Pac',
  TWO: 'Two',
  WHT: 'Wht',
  FEM: 'Fem',
  MAL: 'Mal',
  UNK: 'Unk',
} as const

export type TDemographicLabel =
  (typeof ODemographicChartLabels)[keyof typeof ODemographicChartLabels]

const unknownColor: IPrepareBarChartColors = {
  label: 'Unk',
  color: getComputedColor('--ba-50-dark-gray'),
}

const genderTheme: IPrepareBarChartColors[] = [
  {
    label: ODemographicChartLabels.FEM,
    color: getComputedColor('--ba-secondary'),
  },
  {
    label: ODemographicChartLabels.MAL,
    color: getComputedColor('--ba-darkGray'),
  },
]

const minAndNonMinTheme: IPrepareBarChartColors[] = [
  {
    label: ODemographicChartLabels.MIN,
    color: getComputedColor('--ba-primary'),
  },
  {
    label: ODemographicChartLabels.NON_MIN,
    color: getComputedColor('--ba-darkGray'),
  },
]
const minAndGenderTheme: IPrepareBarChartColors[] = [
  {
    label: ODemographicChartLabels.MIN,
    color: getComputedColor('--ba-primary'),
  },
  {
    label: ODemographicChartLabels.FEM,
    color: getComputedColor('--ba-secondary'),
  },
]

const individualRacesTheme: IPrepareBarChartColors[] = [
  {
    label: ODemographicChartLabels.ASI,
    color: getComputedColor('--ba-asi-violet'),
  },
  {
    label: ODemographicChartLabels.BLK,
    color: getComputedColor('--ba-blk-blue'),
  },
  {
    label: ODemographicChartLabels.HIS,
    color: getComputedColor('--accent'),
  },
  {
    label: ODemographicChartLabels.IND,
    color: getComputedColor('--ba-nat-ind-golden-rod'),
  },
  {
    label: ODemographicChartLabels.PAC,
    color: getComputedColor('--ba-pac-pink'),
  },
  {
    label: ODemographicChartLabels.TWO,
    color: getComputedColor('--ba-two-peach'),
  },
  {
    label: ODemographicChartLabels.WHT,
    color: getComputedColor('--ba-brand-pc-wht-red'),
  },
]

const protectedVeteransHiresAndApplicantsTheme: IPrepareBarChartColors[] = [
  {
    label: 'Protected Veteran Hires',
    color: getComputedColor('--ba-pv-hires'),
  },
  {
    label: 'Protected Veteran Applicants',
    color: getComputedColor('--ba-primary'),
  },
]

const totalHiresAndApplicantsTheme: IPrepareBarChartColors[] = [
  {
    label: 'Total Hires',
    color: getComputedColor('--ba-darkGray'),
  },
  {
    label: 'Total Applicants',
    color: getComputedColor('--ba-tertiary'),
  },
]

const protectedVeteransTheme: IPrepareBarChartColors[] = [
  {
    label: 'Protected Veteran',
    color: getComputedColor('--ba-pv-hires'),
  },
  {
    label: 'Not Protected Veteran',
    color: getComputedColor('--ba-darkGray'),
  },
]

const iwdEmploymentTheme: IPrepareBarChartColors[] = [
  { label: 'Actual', color: getComputedColor('--ba-iwd-hires') },
  { label: 'Expected', color: getComputedColor('--ba-darkGray') },
]

const iwdEmploymentDoughnutTheme: IPrepareBarChartColors[] = [
  { label: 'JGs met', color: getComputedColor('--ba-iwd-hires') },
  {
    label: 'JGs not met',
    color: getComputedColor('--ba-darkGray'),
  },
]
const iwdHiresDoughnutTheme: IPrepareBarChartColors[] = [
  { label: 'IWD', color: getComputedColor('--ba-iwd-hires') },
  { label: 'Not IWD', color: getComputedColor('--ba-darkGray') },
]

const iwdHireStatusTheme: IPrepareBarChartColors[] = [
  {
    label: 'IWD Hires',
    color: getComputedColor('--ba-iwd-hires'),
  },
  {
    label: 'Not IWD Hires',
    color: getComputedColor('--ba-secondaryBluegreen'),
  },
]

const iwdApplicantStatusTheme: IPrepareBarChartColors[] = [
  {
    label: 'IWD Applicants',
    color: getComputedColor('--ba-iwd-applicants'),
  },
  {
    label: 'Not IWD Applicants',
    color: getComputedColor('--ba-darkGray'),
  },
]

export const OChartColorThemes: Record<string, IPrepareBarChartColors[]> = {
  GENDER: genderTheme,
  GENDER_WITH_UNKNOWN: [...genderTheme, unknownColor],
  MIN_AND_NON_MIN: minAndNonMinTheme,
  MIN_AND_NON_MIN_WITH_UNKNOWN: [...minAndNonMinTheme, unknownColor],
  MIN_AND_GENDER: minAndGenderTheme,
  INDIVIDUAL_RACES_AND_GENDER: [...individualRacesTheme, genderTheme[0]],
  INDIVIDUAL_RACES: individualRacesTheme,
  INDIVIDUAL_RACES_WITH_UNKNOWN: [...individualRacesTheme, unknownColor],
  HIRES_AND_APPLICANTS: [
    protectedVeteransHiresAndApplicantsTheme[0],
    totalHiresAndApplicantsTheme[0],
    protectedVeteransHiresAndApplicantsTheme[1],
    totalHiresAndApplicantsTheme[1],
  ],
  IWD_HIRES_AND_APPLICANTS: [...iwdHireStatusTheme, ...iwdApplicantStatusTheme],
  IWD_EMPLOYMENT: iwdEmploymentTheme,
  IWD_EMPLOYMENT_DOUGHNUT: iwdEmploymentDoughnutTheme,
  IWD_HIRES_DOUGHNUT: iwdHiresDoughnutTheme,
  PROTECTED_VETERANS: protectedVeteransTheme,
}
export interface IDemographicColor {
  label: string
  color: string
  /** @matches is there a specific key that this label should represent? */
  matches?: string
}
export interface IPrepareBarChartColors extends IDemographicColor {
  displayName?: string
}

export interface IPrepareBarChart {
  title: string
  subtitle: string
  resultsBy?: EnumerationsDataVisualizationResultsBy
  isThick?: boolean
  isSkinny?: boolean
}

export interface IPrepareBarChartOptions extends IPrepareBarChart {
  colors: IPrepareBarChartColors[]
  code: TCode
  isStacked?: boolean
  insertSpacer?: boolean
  spacerIndex?: number
  usePercents?: boolean | { active: boolean; multiplyBy100?: boolean }
}

export interface IPrepareStackedBarChartOptions extends IPrepareBarChart {
  /** @attributes basically represents color and display of labels */
  attributes: {
    label: string
    data: { value: string | number; color: string; display: string }[]
  }[]
  hasNegatives?: boolean
  labelKey: string
  uniqueKey: string
}

export interface IPrepareMultiStackedBarChartOptions {
  countKeys: { label: string; countKey: string }[]
  selectedDemographics: string[]
  /** @attributes basically represents color and display of labels */
  attributes: {
    label: string
    demographic: string
    stackColors: string[]
  }[]
}

export interface IPrepareBarChartDemographicCount {
  demographic: string
  percent: number
  count: number
  resultsByToDisplayFor: EnumerationsDataVisualizationResultsBy | null
}

export interface IPrepareBarChartData {
  label: string
  demographicCounts: IPrepareBarChartDemographicCount[]
  code: string
}

export type TGroupedData = { [key: string]: number[] }
export type TResultsBy = 'total-minorities' | 'individual-races'
export type TProtectedClass = 'race' | 'gender' | 'race-and-gender'
export type TChartType = 'bar' | 'doughnut' | 'brady-bunch'
interface IDrillDownDataStructure {
  dataLocation: string
  title: string
  appendTitle: boolean
  subtitle?: string
  breadcrumbs: string[]
  chartData: string
  tiles: {
    [n: number]: {
      titleCopy: string
      display: string
      subDisplay: string
      subDisplayCopy?: string
    }
  }
}

export type TEmploymentDrilldownSpecification = Record<
  'bar',
  Record<
    number,
    Record<Exclude<TProtectedClass, 'race-and-gender'>, IDrillDownDataStructure>
  >
>
interface IDrilldownLevelOptions {
  [level: number]: {
    func: (...args: any) => Promise<any>
    data: any
  }
}
export type TDrillDownFetchFunctionSpecification = Record<
  TChartType,
  IDrilldownLevelOptions
>
export interface ILegendColor {
  label: string
  color: string
}

export type TColorOptions = Record<
  TProtectedClass,
  Record<TResultsBy, IPrepareBarChartColors[]>
>

export type TOnSortChart = (
  sortValue: Ref<ILabelValue>,
  flatData: ComputedRef<Record<string, number>[]>,
  chartData: Ref<IGroupBarChartData | null>
) => Record<string, number>[]

export type ChartCarouselType = 'drilldown' | 'stacked' | 'barchart' | 'wfa-bar'
